/**
 * Colour handling for the widget: validating what customers type, and deriving the
 * player's palette from it and from the page it sits on.
 *
 * Every colour is resolved here to an absolute value and handed to the stylesheet as
 * a private `--ba-x-*` variable. The stylesheet's public variables (`--ba-accent` and
 * friends) fall back to those, so a host page that overrides a public variable in its
 * own CSS still wins, as documented.
 *
 * Why not derive the palette in CSS from `currentColor`? A custom property holding
 * `currentColor` resolves it at each element that uses it, not where it was declared,
 * so the play button — which sets its own `color` for the icon — would paint its
 * background in its icon colour. Absolute values have no such trap, and they work in
 * browsers without `color-mix()`.
 */
import {
  DEFAULT_PLAYER_DESIGN,
  DEFAULT_PLAYER_THEME,
  PLAYER_DESIGNS,
  PLAYER_FONTS,
  PLAYER_THEMES,
} from "@narratenow/shared";
import type {
  PlayerDesign,
  PlayerFont,
  PlayerTheme,
  SiteConfigResponse,
  WidgetCustomization,
} from "@narratenow/shared";

/** Accept #rgb / #rrggbb only — anything else is ignored rather than trusted. */
export function normalizeHexColor(value: string | undefined): string {
  if (!value) return "";
  const hex = value.trim();
  if (!/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) return "";
  if (hex.length !== 4) return hex.toLowerCase();
  // Expand #abc to #aabbcc so the colour math below has one shape to handle.
  return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toLowerCase();
}

export function isPlayerDesign(value: unknown): value is PlayerDesign {
  return (PLAYER_DESIGNS as readonly unknown[]).includes(value);
}

export function isPlayerTheme(value: unknown): value is PlayerTheme {
  return (PLAYER_THEMES as readonly unknown[]).includes(value);
}

function isPlayerFont(value: unknown): value is PlayerFont {
  return (PLAYER_FONTS as readonly unknown[]).includes(value);
}

// ── Colour math ─────────────────────────────────────────────────

/** [r, g, b] in 0–255 and alpha in 0–1. */
type Rgba = [number, number, number, number];

const LIGHT_INK: Rgba = [22, 24, 29, 1]; // #16181d — text on light pages
const DARK_INK: Rgba = [242, 243, 245, 1]; // #f2f3f5 — text on dark pages
const WHITE: Rgba = [255, 255, 255, 1];
const DARK_PAPER: Rgba = [24, 25, 29, 1]; // #18191d

/**
 * Parse the colour strings this code meets: hex from the dashboard, and the
 * `rgb()` / `rgba()` that getComputedStyle reports. Anything else — keywords,
 * `oklch()`, `color()` — comes back null and the caller falls back.
 */
function parseColor(value: string | null | undefined): Rgba | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  if (v === "transparent") return [0, 0, 0, 0];

  const hex = normalizeHexColor(v);
  if (hex) {
    const n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff, 1];
  }

  const m =
    /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?\s*\)$/.exec(
      v,
    );
  if (!m) return null;
  let alpha = 1;
  if (m[4] !== undefined) {
    alpha = m[4].endsWith("%") ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
  }
  return [+m[1], +m[2], +m[3], Math.max(0, Math.min(1, alpha))];
}

/** `#rrggbb` when opaque, `rgba(…)` otherwise. Stable output, so tests can compare. */
function format(c: Rgba): string {
  const [r, g, b, a] = c.map((v, i) => (i < 3 ? Math.round(v) : v)) as Rgba;
  if (a >= 1) {
    return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
  }
  return `rgba(${r}, ${g}, ${b}, ${Math.round(a * 1000) / 1000})`;
}

/** Linear blend from `a` toward `b` by `amount` (0 = a, 1 = b). */
function mix(a: Rgba, b: Rgba, amount: number): Rgba {
  return [0, 1, 2, 3].map((i) => a[i] + (b[i] - a[i]) * amount) as Rgba;
}

function withAlpha(c: Rgba, alpha: number): Rgba {
  return [c[0], c[1], c[2], alpha];
}

/** Paint a translucent colour onto an opaque one. */
function flatten(c: Rgba, onto: Rgba): Rgba {
  return [...mix(onto, c, c[3]).slice(0, 3), 1] as Rgba;
}

/** WCAG relative luminance. */
function luminance(c: Rgba): number {
  const [r, g, b] = c.slice(0, 3).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two opaque colours, 1–21. */
function contrast(a: Rgba, b: Rgba): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Below this, white text reads better than black. */
function isDark(c: Rgba): boolean {
  return luminance(c) < 0.179;
}

/** Whichever of the two inks reads better on `bg`. */
function inkFor(bg: Rgba): Rgba {
  return contrast(LIGHT_INK, bg) >= contrast(DARK_INK, bg)
    ? LIGHT_INK
    : DARK_INK;
}

/** Colour for an icon drawn on `fill`: `preferred` if it reads, else black or white. */
export function onColor(fill: string, preferred?: string): string {
  const f = parseColor(fill);
  if (!f) return "#ffffff";
  const p = parseColor(preferred);
  if (p && p[3] >= 1 && contrast(p, f) >= 4.5) return format(p);
  return contrast(WHITE, f) >= contrast(LIGHT_INK, f)
    ? "#ffffff"
    : format(LIGHT_INK);
}

// ── Reading the page ────────────────────────────────────────────

export interface PageColors {
  /** The page's text colour. */
  ink: Rgba;
  /** The first opaque background behind the player, when one can be read. */
  bg: Rgba | null;
}

/**
 * The colours the player sits among, read from `context` — the element the player is
 * mounted in, or `<body>` for the floating button.
 *
 * Text colour is the reliable signal: it inherits, so the context element reports
 * whatever the page set. Backgrounds do not inherit and are often an image or a
 * gradient, so they are only used as a sanity check when one can be read.
 */
export function samplePage(context: Element): PageColors {
  const read = (el: Element, prop: "color" | "backgroundColor") => {
    try {
      return parseColor(getComputedStyle(el)[prop]);
    } catch {
      return null;
    }
  };

  let bg: Rgba | null = null;
  for (let el: Element | null = context; el; el = el.parentElement) {
    const c = read(el, "backgroundColor");
    if (c && c[3] >= 0.5) {
      bg = flatten(c, WHITE);
      break;
    }
  }

  let ink = read(context, "color") ?? LIGHT_INK;
  if (ink[3] < 1) ink = flatten(ink, bg ?? (isDark(ink) ? WHITE : DARK_PAPER));

  // Some themes colour only their paragraphs and leave the default black on
  // everything else. If the article's text disagrees about light vs dark, believe it.
  const paragraph = Array.from(document.querySelectorAll("p")).find(
    (p) => !p.closest(".ba-widget") && (p.textContent ?? "").trim().length > 40,
  );
  const pInk = paragraph ? read(paragraph, "color") : null;
  if (pInk && pInk[3] >= 0.5 && isDark(pInk) !== isDark(ink))
    ink = flatten(pInk, WHITE);

  // Last resort: text that does not read on the background we can see.
  if (bg && contrast(ink, bg) < 3) ink = inkFor(bg);

  return { ink, bg };
}

// ── Resolving the appearance ────────────────────────────────────

export interface Appearance {
  design: PlayerDesign;
  theme: PlayerTheme;
  font: PlayerFont;
  /** Background set to "transparent". */
  transparent: boolean;
  /** Private CSS variables, without the `--ba-x-` prefix. */
  vars: Record<string, string>;
}

/**
 * Every private variable the widget may set. Used to remove the ones a new
 * appearance no longer sets, so clearing a colour really clears it.
 */
const PRIVATE_VARS = [
  "ink",
  "paper",
  "bg",
  "bg-hover",
  "border",
  "text",
  "text-secondary",
  "accent",
  "accent-hover",
  "accent-light",
  "on-accent",
  "surface",
  "track",
  "wave-idle",
  "title",
  "time",
  "disclaimer",
  "danger",
  "shadow",
] as const;

/**
 * Turn a customization record into the player's classes and colours.
 *
 * `page` is only called for the auto theme, and only then does the widget touch the
 * host's computed styles.
 */
export function resolveAppearance(
  wc: WidgetCustomization,
  page: () => PageColors,
): Appearance {
  const design = isPlayerDesign(wc.design) ? wc.design : DEFAULT_PLAYER_DESIGN;
  const theme = isPlayerTheme(wc.theme) ? wc.theme : DEFAULT_PLAYER_THEME;
  const font = isPlayerFont(wc.fontFamily) ? wc.fontFamily : "inherit";

  // Ink is the text colour; paper is the opaque colour behind it, used for the
  // floating panel and the popovers.
  let ink: Rgba;
  let paper: Rgba;
  let opaque: boolean;
  if (theme === "light") {
    [ink, paper, opaque] = [LIGHT_INK, WHITE, true];
  } else if (theme === "dark") {
    [ink, paper, opaque] = [DARK_INK, DARK_PAPER, true];
  } else {
    const sampled = page();
    ink = sampled.ink;
    // The page's own background makes the best paper, when it is readable under
    // the ink — the panel then looks like part of the page.
    paper =
      sampled.bg && contrast(ink, sampled.bg) >= 4.5
        ? sampled.bg
        : isDark(ink)
          ? WHITE
          : DARK_PAPER;
    opaque = false;
  }

  const transparent = wc.backgroundColor === "transparent";
  const chosenBg = parseColor(normalizeHexColor(wc.backgroundColor));
  if (chosenBg) {
    paper = chosenBg;
    // Text has to read on the background the customer chose, whatever the theme
    // thinks — dark page text would otherwise vanish on a dark custom background.
    if (contrast(ink, chosenBg) < 4.5) ink = inkFor(chosenBg);
  }

  const accent = parseColor(normalizeHexColor(wc.accentColor)) ?? ink;
  // Hover moves toward the paper, or toward the ink when the accent is already
  // close to the paper — darkening does nothing visible to near-black ink.
  const hover =
    parseColor(normalizeHexColor(wc.hoverColor)) ??
    mix(accent, contrast(accent, paper) >= 3 ? paper : ink, 0.22);

  const surface = transparent
    ? null
    : (chosenBg ?? (opaque ? mix(paper, ink, 0.04) : withAlpha(ink, 0.045)));

  const vars: Record<string, string> = {
    ink: format(ink),
    paper: format(paper),
    bg: format(paper),
    "bg-hover": format(withAlpha(ink, 0.06)),
    border: format(withAlpha(ink, transparent ? 0.16 : 0.11)),
    text: format(ink),
    "text-secondary": format(withAlpha(ink, 0.64)),
    accent: format(accent),
    "accent-hover": format(hover),
    "accent-light": format(withAlpha(accent, 0.1)),
    "on-accent": onColor(format(accent), format(paper)),
    surface: surface ? format(surface) : "transparent",
    track: format(
      parseColor(normalizeHexColor(wc.trackColor)) ?? withAlpha(ink, 0.18),
    ),
    "wave-idle": format(
      parseColor(normalizeHexColor(wc.waveIdleColor)) ?? withAlpha(accent, 0.5),
    ),
    danger: isDark(paper) ? "#ff8a80" : "#c62828",
    shadow: isDark(paper)
      ? "0 6px 28px rgba(0, 0, 0, 0.55)"
      : "0 4px 24px rgba(0, 0, 0, 0.12)",
  };

  // Only explicit choices are set for these. Left unset, the stylesheet chains them
  // to --ba-text / --ba-text-secondary, so a host page that restyles those through
  // CSS restyles the label and timestamps along with them.
  const explicit: [string, string | undefined][] = [
    ["title", wc.titleColor],
    ["time", wc.timeColor],
    ["disclaimer", wc.disclaimerColor],
  ];
  for (const [name, value] of explicit) {
    const hex = normalizeHexColor(value);
    if (hex) vars[name] = hex;
  }

  return { design, theme, font, transparent, vars };
}

/** Write an appearance onto the widget root: classes, and every private variable. */
export function applyAppearance(root: HTMLElement, a: Appearance): void {
  for (const cls of Array.from(root.classList)) {
    if (/^ba-(design|theme|font)--/.test(cls)) root.classList.remove(cls);
  }
  root.classList.add(
    `ba-design--${a.design}`,
    `ba-theme--${a.theme}`,
    `ba-font--${a.font}`,
  );
  root.classList.toggle("ba-bg--transparent", a.transparent);

  for (const name of PRIVATE_VARS) {
    const value = a.vars[name];
    if (value) root.style.setProperty(`--ba-x-${name}`, value);
    else root.style.removeProperty(`--ba-x-${name}`);
  }
}

// ── Remembered config ───────────────────────────────────────────

const CACHE_PREFIX = "narratenow:cfg:v1:";
const CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * The last site config this browser received, so a returning visitor's first frame
 * is already in the site's design rather than the default.
 *
 * Holds nothing about the reader: it is the same public record every visitor gets.
 * Storage can be missing or throw (private mode, a sandboxed preview), and the value
 * may have been written by an older widget, so every read is defensive.
 */
export function readCachedConfig(siteId: string): SiteConfigResponse | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + siteId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { t?: unknown; config?: unknown };
    if (
      typeof parsed?.t !== "number" ||
      Date.now() - parsed.t > CACHE_MAX_AGE_MS
    ) {
      return null;
    }
    const c = parsed.config as Partial<SiteConfigResponse> | null;
    if (
      !c ||
      typeof c !== "object" ||
      typeof c.hasWatermark !== "boolean" ||
      !Array.isArray(c.allowedVoices) ||
      !c.allowedVoices.every((v) => v === "male" || v === "female") ||
      (c.widgetCustomization !== undefined &&
        (typeof c.widgetCustomization !== "object" ||
          c.widgetCustomization === null))
    ) {
      return null;
    }
    return c as SiteConfigResponse;
  } catch {
    return null;
  }
}

export function writeCachedConfig(
  siteId: string,
  config: SiteConfigResponse,
): void {
  try {
    localStorage.setItem(
      CACHE_PREFIX + siteId,
      JSON.stringify({ t: Date.now(), config }),
    );
  } catch {
    // Storage full or unavailable: the next visit simply paints from the network.
  }
}
